/**
 * Identifiant stable d'un établissement — miroir de scripts/_stable_id.py.
 *
 * ⚠️ Les deux implémentations doivent produire exactement le même résultat.
 *    Le Python génère les identifiants figés dans establishments.json ; ce
 *    module sert à les vérifier. Une divergence rendrait des dossiers
 *    introuvables, silencieusement. tests/stable-id.test.js les compare sur
 *    les 816 établissements réels — c'est ce test qui tient la promesse.
 *
 * L'application n'appelle pas stableId() à l'exécution : les identifiants
 * arrivent tout faits dans le payload. Ce module existe pour le test croisé,
 * et pour outiller un diagnostic.
 *
 * Voir _stable_id.py pour le raisonnement : pourquoi le nom, et pas l'adresse
 * ni les coordonnées.
 */

export const ID_LENGTH = 12

/**
 * Nom -> forme canonique. Doit reproduire normalize_name() du Python :
 *
 *   NFKD, puis suppression des caractères non-ASCII (les accents deviennent
 *   des diacritiques combinants que le filtre élimine), majuscules, tout ce
 *   qui n'est pas [A-Z0-9] remplacé par une espace, bords rognés.
 */
export function normalizeName(nom) {
  return String(nom ?? '')
    .normalize('NFKD')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, '') // équivalent de .encode('ascii', 'ignore')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
}

/**
 * Identifiant stable de 12 caractères hexadécimaux.
 *
 * Asynchrone parce que l'API SubtleCrypto du navigateur l'est ; le Python est
 * synchrone, ce qui n'a aucune incidence sur le résultat.
 *
 * @returns {Promise<string>}
 */
const KINDS = new Set(['gmf', 'hosp', 'dept'])

export async function stableId(kind, nom) {
  if (!KINDS.has(kind)) throw new Error(`kind inattendu : ${kind}`)
  const payload = new TextEncoder().encode(`${kind}|${normalizeName(nom)}`)
  const digest = await crypto.subtle.digest('SHA-256', payload)
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, ID_LENGTH)
}

/**
 * Identifiant d'un département : haché sur (hôpital, nom).
 *
 * Le hachage plutôt qu'un UUID : rajouter « Urgence » à un hôpital qui en a
 * déjà une retombe sur le même identifiant, et la clé primaire refuse le
 * doublon d'elle-même. Deux personnes qui créent « Urgence » en même temps
 * dans le même hôpital créent la même ligne, pas deux.
 *
 * Le nom est normalisé comme partout : « Urgence » et « URGENCES » — non,
 * justement pas. Voir normalizeName : le pluriel change le hachage. C'est
 * assumé, la liste de suggestions du champ existe pour ça.
 */
export function departmentId(hospitalId, deptName) {
  return stableId('dept', `${hospitalId}|${deptName}`)
}
